/*
 * @Author: lumiaomei@leedarson.com
 * @Date: 2023-04-06 20:03:51
 * Copyright © Leedarson. All rights reserved.
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useLogin from './useLogin';
import Toast from '@root/components/toast';
import {
  SECONDARY_CERTIFICATION_GET_CODE_TIME,
  SECONDARY_CERTIFICATION_SIGN,
} from '@root/constant';
import { resendSecondCertification } from '@root/api/login';

const COUNT_DOWN = 60 * 10;

const useSecondCertification = () => {
  const { t } = useTranslation();
  const { loginWithSecondCertification } = useLogin();
  const [resend, setResend] = useState(false);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const secondaryCertificationSign = useMemo(() => {
    return window.localStorage.getItem(SECONDARY_CERTIFICATION_SIGN) || '';
  }, []);

  const defaultCountDown = useMemo(() => {
    if (!secondaryCertificationSign) {
      setResend(true);
      return COUNT_DOWN;
    }

    const _time = window.localStorage.getItem(
      SECONDARY_CERTIFICATION_GET_CODE_TIME,
    );
    if (!_time) {
      window.localStorage.setItem(
        SECONDARY_CERTIFICATION_GET_CODE_TIME,
        `${Date.now()}`,
      );
      return COUNT_DOWN;
    }

    const _left = Math.floor(COUNT_DOWN - (Date.now() - +_time) / 1000);

    // 还在10分钟内不需要重新取
    if (_left > 1) {
      return _left;
    }

    setResend(true);
    return COUNT_DOWN;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resend]);

  const onIntervalEnd = useCallback(() => {
    setResend(true);
  }, []);

  const handleCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e && e.target;
      setCode(value);
    },
    [],
  );

  // 重新获取code
  const handleResendCode = useCallback(async () => {
    window.localStorage.removeItem(SECONDARY_CERTIFICATION_GET_CODE_TIME);
    setResend(false);
    setCode('');
    try {
      await resendSecondCertification(secondaryCertificationSign);
      Toast.info(t('Verify_Code_ResendTip'));
    } catch {
      setResend(true);
      Toast.info(t('Verify_Code_ResendFail'));
    }
  }, [secondaryCertificationSign, t]);

  const onSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await loginWithSecondCertification({ code, secondaryCertificationSign });
      window.localStorage.removeItem(SECONDARY_CERTIFICATION_SIGN);
      window.localStorage.removeItem(SECONDARY_CERTIFICATION_GET_CODE_TIME);
    } catch (e: any) {
      const error = e.response?.data || {};
      setSubmitting(false);
      console.log(error);
      const errorCode = error.code;
      if (errorCode === 560118) {
        setResend(true);
      }

      const errorDesc = t('Incorrect_Code');

      Toast.info(
        t(`Verify_Code_CodeError_${errorCode}`, { defaultValue: errorDesc }),
      );
    }
  }, [
    code,
    loginWithSecondCertification,
    secondaryCertificationSign,
    submitting,
    t,
  ]);

  const onKeyDownCode = useCallback(
    (e: any) => {
      const code = e.keyCode || e.which || e.charCode;
      if (+code === 13) {
        onSubmit();
      }
    },
    [onSubmit],
  );

  return {
    resend,
    code,
    submitting,
    defaultCountDown,
    onIntervalEnd,
    handleResendCode,
    handleCodeChange,
    onKeyDownCode,
    onSubmit,
  };
};

export default useSecondCertification;
